import React, { useState, useCallback } from "react";
import { ButtonH } from "components/UiKit/ButtonH";
import { Ico } from "components/Uikit";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import {
    diagnoseFolderApi,
    applyDiagnoseFixesApi,
    DiagnoseIssue,
} from "api/fastApi/healthCheck";
import { message, Modal, Table, Tag, Checkbox, Tooltip } from "antd";
import { MetadataToSubtitlesLanguages } from "providers/AudioProvider/types";
import { useIsEnglishUi } from "utils/uiLanguage";
import { useAutoEditBlock } from "../ManualOnly/AutoEditGate";

const spinKeyframes = `
@keyframes diagSpin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}
`;

export const DiagnoseButton: React.FC = () => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const [loading, setLoading] = useState(false);
    const [fixLoading, setFixLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [issues, setIssues] = useState<DiagnoseIssue[]>([]);
    const [selectedFixIds, setSelectedFixIds] = useState<Set<string>>(new Set());
    const [summary, setSummary] = useState<{ total: number; auto_fixable: number; flagged_only: number } | null>(null);

    const fromEng = useIsEnglishUi();
    // Проверка только показывает — она работает всегда. «Исправить выбранные» записывает
    // машинные правки (раздвигает, переводит, озвучивает) — в ручном режиме выключено.
    const autoEdit = useAutoEditBlock();

    const labels = {
        button: fromEng ? "Scan" : "Проверить",
        hint: fromEng
            ? "Scan selected rows (or entire folder) for issues: overlaps, missing translations, voice mismatches, etc."
            : "Сканировать выбранные строки (или всю папку) на проблемы: наложения, отсутствие перевода, несовпадение голоса и т.д.",
        modalTitle: fromEng ? "Folder Diagnosis" : "Диагностика папки",
        fixSelected: fromEng ? "Fix Selected" : "Исправить выбранные",
        cancel: fromEng ? "Cancel" : "Отмена",
        selectAll: fromEng ? "Select all fixable" : "Выбрать все исправимые",
        noIssues: fromEng ? "No issues found" : "Проблем не найдено",
        fixSuccess: fromEng ? "Fixes applied, reloading..." : "Исправления применены, загрузка данных...",
        fixError: fromEng ? "Failed to apply fixes" : "Ошибка применения исправлений",
        scanError: fromEng ? "Diagnosis failed" : "Ошибка диагностики",
        russian: fromEng ? "Source" : "Источник",
        translation: fromEng ? "Translation" : "Перевод",
        fixesOff: fromEng
            ? "Fixing is off in the “No automatic edits” mode: the list is here so you can fix it by hand."
            : "Исправления выключены режимом „Без автоправок“: список показан, чтобы поправить руками.",
    };

    const typeLabels: Record<string, string> = fromEng
        ? {
              r3_overlap: "Overlap",
              r1_dash_split: "Split at --",
              r2_too_long: "Too long",
              r5_long_gap: "Long gap",
              t1_missing_translation: "Missing translation",
              t2_missing_audio: "Missing audio",
              t3_orphan: "Orphaned translation",
              t5_voice_mismatch: "Voice mismatch",
              t6_voiceover_overflow: "Audio too long",
              t7_uniform_speed: "Fix all speeds",
          }
        : {
              r3_overlap: "Наложение",
              r1_dash_split: "Разрыв на --",
              r2_too_long: "Слишком длинный",
              r5_long_gap: "Длинная пауза",
              t1_missing_translation: "Нет перевода",
              t2_missing_audio: "Нет аудио",
              t3_orphan: "Сирота",
              t5_voice_mismatch: "Несовпадение голоса",
              t6_voiceover_overflow: "Озвучка длиннее слота",
              t7_uniform_speed: "Исправить все скорости",
          };

    const selectedTracks = [
        ...state.selectedOriginalTrackIds,
        ...state.selectedTranslatedTrackIds,
    ];
    const hasSelection = selectedTracks.length > 0;

    const getSubtitleIds = (): string[] | undefined => {
        if (!hasSelection) return undefined;
        return selectedTracks.map((s) => s.track.original_id || s.track.id);
    };

    const handleDiagnoseClick = useCallback(async () => {
        if (!state.mediaId || !state.folder || !state.lang) {
            message.error(labels.scanError);
            return;
        }

        setLoading(true);
        try {
            const res = await diagnoseFolderApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
                subtitle_ids: getSubtitleIds(),
            });

            if (res.error) {
                message.error(res.error);
                return;
            }

            setIssues(res.issues);
            setSummary(res.summary);

            // Pre-select all auto-fixable — unless nothing may be applied in this mode.
            const fixable = new Set(
                autoEdit.blocked ? [] : res.issues.filter((i) => i.auto_fixable).map((i) => i.fix_id)
            );
            setSelectedFixIds(fixable);
            setShowModal(true);
        } catch (error: any) {
            console.error("Diagnose error:", error);
            message.error(labels.scanError);
        } finally {
            setLoading(false);
        }
    }, [state.mediaId, state.folder, state.lang, state.originalLang, selectedTracks.length, autoEdit.blocked]);

    const handleFixSelected = async () => {
        if (autoEdit.blocked || selectedFixIds.size === 0) return;

        setFixLoading(true);
        try {
            const res = await applyDiagnoseFixesApi({
                mediaId: state.mediaId,
                folderNum: parseInt(state.folder.toString(), 10),
                lang: state.lang,
                language_from: MetadataToSubtitlesLanguages[state.originalLang],
                fix_ids: Array.from(selectedFixIds),
            });

            setShowModal(false);
            message.success(labels.fixSuccess);

            await handles.refreshAllSubtitles();
            handles.setSelectedOriginalTrackIds([]);
            handles.setSelectedTranslatedTrackIds([]);

            if (res.failed?.length) {
                message.warning(
                    `${res.failed.length} fix(es) failed: ${res.failed.map((f) => f.error).join(", ")}`
                );
            }
        } catch (error: any) {
            console.error("Fix error:", error);
            message.error(labels.fixError);
        } finally {
            setFixLoading(false);
        }
    };

    const toggleFix = (fixId: string) => {
        setSelectedFixIds((prev) => {
            const next = new Set(prev);
            if (next.has(fixId)) next.delete(fixId);
            else next.add(fixId);
            return next;
        });
    };

    const selectAllFixable = () => {
        const fixable = issues.filter((i) => i.auto_fixable).map((i) => i.fix_id);
        setSelectedFixIds(new Set(fixable));
    };

    const scrollToSubtitle = (startMs?: number) => {
        if (startMs == null) return;
        const zoomLevel = 100 - (state as any).zoomLevel;
        const px = startMs / zoomLevel;
        // Find the scrollable subtitles container (left or right column)
        const containers = document.querySelectorAll('[class*="subtitles_block"]');
        if (containers.length === 0) {
            // Fallback: scroll all scrollable divs in the main area
            const scrollable = document.querySelector('.x_scroll_y');
            if (scrollable) {
                scrollable.scrollTop = Math.max(0, px - 100);
            }
        }
        containers.forEach((el) => {
            el.scrollTop = Math.max(0, px - 100);
        });
    };

    const columns = [
        {
            title: "",
            key: "select",
            width: 40,
            render: (_: unknown, record: DiagnoseIssue) =>
                record.auto_fixable ? (
                    <Checkbox
                        checked={selectedFixIds.has(record.fix_id)}
                        disabled={autoEdit.blocked}
                        onChange={() => toggleFix(record.fix_id)}
                    />
                ) : null,
        },
        {
            title: fromEng ? "Side" : "Сторона",
            key: "side",
            width: 100,
            render: (_: unknown, record: DiagnoseIssue) => (
                <Tag color={record.side === "russian" ? "blue" : "green"}>
                    {record.side === "russian" ? labels.russian : labels.translation}
                </Tag>
            ),
        },
        {
            title: fromEng ? "Type" : "Тип",
            key: "type",
            width: 160,
            render: (_: unknown, record: DiagnoseIssue) => (
                <Tag color={record.auto_fixable ? "processing" : "warning"}>
                    {typeLabels[record.type] || record.type}
                </Tag>
            ),
        },
        {
            title: fromEng ? "Description" : "Описание",
            dataIndex: "description",
            key: "description",
            ellipsis: true,
        },
    ];

    const selCount = hasSelection ? selectedTracks.length : 0;
    const buttonLabel = selCount > 0 ? `${labels.button} (${selCount})` : labels.button;

    const helpIconStyle: React.CSSProperties = {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "1.1em",
        height: "1.1em",
        borderRadius: "50%",
        border: "1px solid #888",
        fontSize: "0.75em",
        lineHeight: 1,
        cursor: "help",
        userSelect: "none",
    };

    return (
        <>
            <style>{spinKeyframes}</style>
            <div className="_f _fm" style={{ gap: 6 }}>
                <ButtonH sm ghost onClick={handleDiagnoseClick} disabled={loading}>
                    <Ico
                        size={"16"}
                        style={
                            loading
                                ? { animation: "diagSpin 1s linear infinite", display: "inline-block" }
                                : undefined
                        }
                    >
                        troubleshoot
                    </Ico>
                    {buttonLabel}
                </ButtonH>
                <Tooltip title={labels.hint}>
                    <span style={helpIconStyle}>?</span>
                </Tooltip>
            </div>

            <Modal
                title={
                    <span>
                        {labels.modalTitle}
                        {hasSelection && (
                            <Tag color="blue" style={{ marginLeft: 8 }}>
                                {selCount} {fromEng ? "selected" : "выбрано"}
                            </Tag>
                        )}
                        {!hasSelection && (
                            <Tag color="orange" style={{ marginLeft: 8 }}>
                                {fromEng ? "Entire folder" : "Вся папка"}
                            </Tag>
                        )}
                    </span>
                }
                open={showModal}
                onOk={handleFixSelected}
                onCancel={() => setShowModal(false)}
                okText={`${labels.fixSelected} (${selectedFixIds.size})`}
                cancelText={labels.cancel}
                width={900}
                okButtonProps={{
                    disabled: autoEdit.blocked || selectedFixIds.size === 0,
                    loading: fixLoading,
                }}
            >
                {autoEdit.blocked && (
                    <div role="note" style={{ marginBottom: 12, color: "#d4380d", fontSize: 13 }}>
                        {labels.fixesOff}
                    </div>
                )}
                {issues.length === 0 ? (
                    <div style={{ textAlign: "center", padding: 24 }}>
                        <Tag color="green" style={{ fontSize: 14, padding: "4px 12px" }}>
                            {labels.noIssues}
                        </Tag>
                    </div>
                ) : (
                    <>
                        <div
                            style={{
                                marginBottom: 12,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                            }}
                        >
                            <span>
                                {fromEng ? "Total:" : "Всего:"}{" "}
                                <strong>{summary?.total || 0}</strong>
                                {summary?.auto_fixable ? (
                                    <Tag color="blue" style={{ marginLeft: 8 }}>
                                        {summary.auto_fixable} {fromEng ? "fixable" : "исправимых"}
                                    </Tag>
                                ) : null}
                                {summary?.flagged_only ? (
                                    <Tag color="warning" style={{ marginLeft: 4 }}>
                                        {summary.flagged_only} {fromEng ? "flagged" : "отмечено"}
                                    </Tag>
                                ) : null}
                            </span>
                            {!autoEdit.blocked && (
                                <ButtonH sm ghost onClick={selectAllFixable}>
                                    {labels.selectAll}
                                </ButtonH>
                            )}
                        </div>
                        <Table
                            dataSource={issues}
                            columns={columns}
                            rowKey="fix_id"
                            size="small"
                            pagination={false}
                            scroll={{ y: 400 }}
                            rowClassName={(record) =>
                                selectedFixIds.has(record.fix_id)
                                    ? "ant-table-row-selected"
                                    : ""
                            }
                            onRow={(record) => ({
                                onClick: () => scrollToSubtitle(record.start_ms),
                                style: { cursor: record.start_ms != null ? "pointer" : "default" },
                            })}
                        />
                    </>
                )}
            </Modal>
        </>
    );
};
